import { useState, useEffect, useCallback, useRef } from "react";
import { X, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { MeasureTargetsTab } from "./configuration/MeasureTargetsTab";
import { RiskFactorScoreTab } from "./configuration/RiskFactorScoreTab";
import { useResidentTabConfiguration } from "../../hooks/useResidentTabConfiguration";
import type { ConfigurationState } from "../../types/configuration";
import type { ApiService } from "../../services/apiService";
import { apiService as defaultApi } from "../../services/apiService";
import { trackCareQueueEvent } from "../../lib/tracking";

// TODO: Remove this flag and restore Measure Targets tab once measure target feature is ready
const showMeasureTargets = false;

interface ConfigurationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Custom API service instance (for widget usage with different auth) */
  api?: ApiService;
  /** Client ID from token (required for scheduler config) */
  clientId?: string;
  /** Facilities the user has access to */
  facilityOptions?: { id: string; name: string }[];
}

type ConfigTab = "measure-targets" | "risk-factor-score" | "care-assessment-scheduler";

export function ConfigurationModal({ open, onOpenChange, api, clientId, facilityOptions = [] }: ConfigurationModalProps) {
  const service = api ?? defaultApi;
  const [activeTab, setActiveTab] = useState<ConfigTab>(showMeasureTargets ? "measure-targets" : "risk-factor-score");
  const [config, setConfig] = useState<ConfigurationState | null>(null);

  // Care assessment scheduler local state
  const [schedulerWorkflows, setSchedulerWorkflows] = useState<string[]>([]);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  const [schedulerSaving, setSchedulerSaving] = useState(false);
  const [schedulerSaveError, setSchedulerSaveError] = useState(false);
  const [schedulerLoaded, setSchedulerLoaded] = useState(false);
  const [schedulerFacilityId, setSchedulerFacilityId] = useState<string>("");

  // Fetch configuration from the backend when modal is open
  const {
    config: savedConfig,
    isLoading,
    error: configError,
    saveConfig,
    isSaving,
    saveError,
  } = useResidentTabConfiguration({ enabled: open, api });

  // Keep a ref to the initial config returned by the API (for "Reset to Defaults")
  const initialConfigRef = useRef<ConfigurationState | null>(null);

  // Analytics: emit when modal opens
  useEffect(() => {
    if (open) {
      trackCareQueueEvent("care_queue_dialog_opened", { dialogType: "config" });
    }
  }, [open]);

  // Sync fetched config into local editable state when modal opens
  useEffect(() => {
    if (open && savedConfig) {
      const cloned = structuredClone(savedConfig);
      setConfig(cloned);
      initialConfigRef.current = structuredClone(savedConfig);
      setActiveTab(showMeasureTargets ? "measure-targets" : "risk-factor-score");
    }
  }, [open, savedConfig]);

  // Default scheduler facility to first available option
  useEffect(() => {
    if (open && facilityOptions.length > 0 && !schedulerFacilityId) {
      setSchedulerFacilityId(facilityOptions[0].id);
    }
  }, [open, facilityOptions, schedulerFacilityId]);

  // Fetch scheduler config when Care Assessment Scheduler tab is active
  // Re-fetches when the selected facility changes
  useEffect(() => {
    if (!open || activeTab !== "care-assessment-scheduler") return;
    if (!clientId || !schedulerFacilityId) return;

    let cancelled = false;
    setSchedulerLoading(true);
    setSchedulerLoaded(false);
    service
      .getSchedulerConfig(clientId, schedulerFacilityId)
      .then((data) => {
        if (cancelled) return;
        setSchedulerLoaded(true);
        setSchedulerWorkflows(data?.recurring_workflows || []);
      })
      .catch(() => {
        if (!cancelled) setSchedulerLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setSchedulerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, clientId, schedulerFacilityId, service]);

  // Reset scheduler state when modal closes
  useEffect(() => {
    if (!open) {
      setSchedulerLoaded(false);
      setSchedulerSaveError(false);
      setSchedulerFacilityId("");
    }
  }, [open]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open && !isSaving && !schedulerSaving) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, onOpenChange, isSaving, schedulerSaving]);

  const handleClose = useCallback(() => {
    if (!isSaving && !schedulerSaving) {
      trackCareQueueEvent("care_queue_dialog_action", {
        dialogType: "config",
        action: "cancel",
      });
      onOpenChange(false);
    }
  }, [onOpenChange, isSaving, schedulerSaving]);

  const handleSave = useCallback(async () => {
    trackCareQueueEvent("care_queue_dialog_action", {
      dialogType: "config",
      action: "confirm",
    });
    if (activeTab === "care-assessment-scheduler") {
      if (!clientId || !schedulerFacilityId) {
        setSchedulerSaveError(true);
        return;
      }
      setSchedulerSaveError(false);
      setSchedulerSaving(true);
      try {
        await service.saveSchedulerConfig(clientId, schedulerFacilityId, {
          recurring_workflows: schedulerWorkflows,
        });
        onOpenChange(false);
      } catch {
        setSchedulerSaveError(true);
      } finally {
        setSchedulerSaving(false);
      }
      return;
    }

    if (!config) return;
    try {
      await saveConfig(config);
      onOpenChange(false);
    } catch {
      // saveError state is handled by the hook, shown in the UI
    }
  }, [activeTab, config, saveConfig, onOpenChange, service, schedulerWorkflows, clientId, schedulerFacilityId]);

  const handleResetToDefaults = useCallback(() => {
    trackCareQueueEvent("care_queue_config_reset_to_defaults", { tab: activeTab });
    if (activeTab === "care-assessment-scheduler") {
      setSchedulerWorkflows([]);
      return;
    }
    const defaults = initialConfigRef.current;
    if (!defaults) return;
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        riskLevelThresholds: structuredClone(defaults.riskLevelThresholds),
        riskFactorPoints: structuredClone(defaults.riskFactorPoints),
      };
    });
  }, [activeTab]);

  const handleToggleWorkflow = useCallback((workflow: string) => {
    setSchedulerWorkflows((prev) => {
      const wasEnabled = prev.includes(workflow);
      trackCareQueueEvent("care_queue_config_workflow_toggled", {
        workflow,
        enabled: !wasEnabled,
      });
      return wasEnabled ? prev.filter((w) => w !== workflow) : [...prev, workflow];
    });
  }, []);

  if (!open) return null;

  // Show the loading spinner when local config hasn't been set yet. This covers:
  // 1. React Query isLoading (fetch in flight)
  // 2. Modal open but fetch not yet scheduled (RQ v5 enabled switch timing gap)
  // 3. savedConfig arrived but useEffect hasn't synced it to local state yet
  const syncingConfig = !!savedConfig && !config;
  const showLoading = !config && (isLoading || syncingConfig || (open && !configError && !savedConfig));

  const anySaving = isSaving || schedulerSaving;
  const anyError = saveError || schedulerSaveError;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal */}
      <div
        className="relative z-10 flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden"
        style={{ width: "1000px", maxWidth: "calc(100vw - 3rem)", height: "calc(100vh - 80px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Teal Header with Tabs inside */}
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            trackCareQueueEvent("care_queue_config_tab_changed", {
              from: activeTab,
              to: v,
            });
            setActiveTab(v as ConfigTab);
          }}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="shrink-0 bg-primary px-8 pt-5 pb-3 relative">
            <button
              onClick={handleClose}
              className="absolute top-5 right-5 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
              aria-label="Close"
              disabled={anySaving}
            >
              <X className="h-5 w-5" />
            </button>
            <h2
              className="text-primary-foreground"
              style={{ fontSize: "20px", fontWeight: 700 }}
            >
              Configuration
            </h2>
            <p className="text-base text-primary-foreground/90 mt-1">
              Configure risk scoring and assessment scheduling
            </p>
            <TabsList className="mt-4 border-b-0 bg-transparent">
              {showMeasureTargets && (
                <TabsTrigger
                  value="measure-targets"
                  className="!text-white/80 hover:!text-white hover:!bg-white/10 data-[state=active]:!text-white data-[state=active]:!bg-white/15 data-[state=active]:after:!bg-white"
                >
                  Measure Targets
                </TabsTrigger>
              )}
              <TabsTrigger
                value="risk-factor-score"
                className="!text-white/80 hover:!text-white hover:!bg-white/10 data-[state=active]:!text-white data-[state=active]:!bg-white/15 data-[state=active]:after:!bg-white"
              >
                Risk Factor Score Impact
              </TabsTrigger>
              <TabsTrigger
                value="care-assessment-scheduler"
                className="!text-white/80 hover:!text-white hover:!bg-white/10 data-[state=active]:!text-white data-[state=active]:!bg-white/15 data-[state=active]:after:!bg-white"
              >
                Care Assessment Scheduler
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
            {activeTab === "care-assessment-scheduler" ? (
              !clientId || facilityOptions.length === 0 ? (
                <div className="flex items-center justify-center py-24">
                  <span className="text-muted-foreground">
                    No facility information available. Please log in with a facility-enabled account.
                  </span>
                </div>
              ) : schedulerLoading ? (
                <div className="flex items-center justify-center py-24">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-3 text-muted-foreground">Loading scheduler configuration...</span>
                </div>
              ) : schedulerLoaded ? (
                <div className="p-8 space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Recurring Care Assessments</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Enable time-based recurring care assessments for this facility.
                      Assessments are scheduled on a fixed cadence from each resident's admission date.
                    </p>
                    {facilityOptions.length > 1 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">Facility</label>
                        <select
                          aria-label="Select facility"
                          value={schedulerFacilityId}
                          onChange={(e) => {
                            trackCareQueueEvent("care_queue_config_scheduler_facility_changed", {
                              facilityId: e.target.value,
                            });
                            setSchedulerFacilityId(e.target.value);
                          }}
                          className="h-9 px-2 pr-6 border border-solid border-[#D1D2DB] rounded-md text-sm bg-white text-[#1B3647] cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                          {facilityOptions.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.name !== f.id ? `${f.name} (${f.id})` : f.id}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {[
                      { id: "WEEKLY", label: "Weekly", description: "Every 7 days from admission" },
                      { id: "MONTHLY", label: "Monthly", description: "Every 30 days from admission" },
                      { id: "QUARTERLY", label: "Quarterly", description: "Every 92 days from admission" },
                    ].map(({ id, label, description }) => (
                      <label
                        key={id}
                        className="flex items-start gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-gray-300"
                          checked={schedulerWorkflows.includes(id)}
                          onChange={() => handleToggleWorkflow(id)}
                        />
                        <div>
                          <span className="text-sm font-medium">{label}</span>
                          <p className="text-sm text-muted-foreground">{description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-24">
                  <span className="text-destructive">
                    Failed to load scheduler configuration. Please try again.
                  </span>
                </div>
              )
            ) : showLoading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Loading configuration...</span>
              </div>
            ) : config ? (
              <>
                {showMeasureTargets && (
                  <TabsContent value="measure-targets">
                    <MeasureTargetsTab config={config} onConfigChange={setConfig} />
                  </TabsContent>
                )}
                <TabsContent value="risk-factor-score">
                  <RiskFactorScoreTab config={config} onConfigChange={setConfig} />
                </TabsContent>
              </>
            ) : (
              <div className="flex items-center justify-center py-24">
                <span className="text-destructive">
                  Failed to load configuration. Please try again.
                </span>
              </div>
            )}
          </div>
        </Tabs>

        {/* Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-border bg-muted/50">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              {(activeTab === "risk-factor-score" || activeTab === "care-assessment-scheduler") && (
                <Button
                  variant="link"
                  onClick={handleResetToDefaults}
                  className="px-0"
                  disabled={anySaving}
                >
                  Reset to Defaults
                </Button>
              )}
              {anyError && (
                <span className="text-sm text-destructive">
                  Failed to save. Please try again.
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleClose} disabled={anySaving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={anySaving || (activeTab !== "care-assessment-scheduler" && (showLoading || !config))}
              >
                {anySaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Configuration"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
